import { keyframes, createGlobalStyle, styled, css } from 'styled-components';
import { BREAKPOINTS } from '../../../../constants/layout.mjs';
import { theme } from '../../../../constants/theme.mjs';
import { ModalId } from '../../../../contexts/MicrositeMenuContext/types.mjs';
import '../../../Container/index.mjs';
import { MenuButtonWrap } from '../../../Header/MenuItem/MenuItem.styles.mjs';
import '../../../StyleableSvgImage/index.mjs';
import { withCssTransition } from '../DropdownMenu/DropdownMenu.styles.mjs';
import { HeaderMotif } from './HeaderContent.types.mjs';
import { StyleableSvgImage } from '../../../StyleableSvgImage/StyleableSvgImage.mjs';
import { Container } from '../../../Container/Container.mjs';
"use strict";
const FIRST_LEVEL_MENU_HEIGHT = 48;
const ANIMATION_TRANSITION = 0.3;
const addOpacity = keyframes`
  0%{
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  100%{
    opacity: 1;
  }
`;
const removeOverflowHidden = keyframes`
100% {
    overflow: unset;
  }
`;
const removeZeroHeight = keyframes`
  100% {
    max-height: 100%;
  }
`;
const StableGutterGlobalStyles = createGlobalStyle`
  html {
    scrollbar-gutter: stable;
  }
`;
const Content = styled.div`
  position: absolute;
  z-index: ${theme.zIndex.modal};
  width: 100%;
`;
const ContentContainer = styled.div`
  transition: grid-template-rows ${ANIMATION_TRANSITION}s;
  display: grid;
  grid-template-rows: 1fr;
  flex: 0;
  margin: 0 auto;
  position: absolute;
  z-index: ${theme.zIndex.header - 1};
  background-color: ${theme.colors.white};
  width: 100%;
  padding: 0;
  max-width: 100%;

  ${({
  $isCollapsed
}) => $isCollapsed && css`
      grid-template-rows: 0fr;
      opacity: 0;

      ${Content} {
        overflow: hidden;
        max-height: 0;
      }
    `}

  ${({
  $isCollapsed
}) => !$isCollapsed && css`
      ${Content} {
        overflow: hidden;
        animation:
          ${ANIMATION_TRANSITION}s ${removeOverflowHidden} linear forwards,
          ${ANIMATION_TRANSITION}s ${addOpacity} ease-in-out forwards,
          ${ANIMATION_TRANSITION}s ${removeZeroHeight} ease-in-out forwards;
      }
    `}
`;
const StyledContainer = styled.header`
  width: 100%;
  position: absolute;
  padding-bottom: 48px;
  top: env(safe-area-inset-top);

  *:focus,
  * {
    outline: none !important;
  }
`;
const Wrap = styled.nav`
  ${({
  $shouldAnimateBackground
}) => $shouldAnimateBackground && withCssTransition(["background"])}

  align-items: center;
  height: ${FIRST_LEVEL_MENU_HEIGHT}px;
  display: flex;
  justify-content: center;
  position: fixed;
  padding: 0 16px;
  top: env(safe-area-inset-top);
  width: 100%;

  ${({
  $hasWhiteBackground
}) => $hasWhiteBackground && css`
      box-shadow: 0 -1px 0 0 rgb(0 0 0 / 0.05) inset;
      background-color: ${theme.colors.white} !important;
    `}

  @media ${BREAKPOINTS.TOP_NAV_MOBILE_AND_DOWN} {
    ${({
  $isActive
}) => $isActive && css`
        box-shadow: 0 -1px 0 0 rgb(0 0 0 / 0.05) inset;
      `}
  }
`;
const LeftWrap = styled.div`
  align-items: center;
  display: flex;
  gap: 5px;
  height: 100%;
`;
const LogoAnchor = styled.a`
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover {
      color: ${theme.colors.menu.active["light"]};
      ${withCssTransition(["color"])};
    }
  }

  color: ${({
  $motif
}) => $motif === HeaderMotif.LightText ? theme.colors.white : theme.colors.black};
  outline: none;

  &:active,
  &:focus {
    color: ${({
  $motif
}) => $motif === HeaderMotif.LightText ? theme.colors.white : theme.colors.black};
  }

  @media (hover: none) {
    &:focus,
    &:active {
      box-shadow: none;
      background: inherit;
      color: ${theme.colors.black};
    }
  }

  margin-right: 35px;
  height: 100%;
  display: flex;
  align-items: center;

  @media ${BREAKPOINTS.TOP_NAV_MOBILE_AND_DOWN} {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin-right: 0;
  }
`;
const ItemsWrap = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 100%;

  @media ${BREAKPOINTS.TOP_NAV_MOBILE_AND_DOWN} {
    display: none;
  }
`;
const ItemLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 7.5px;
  color: ${theme.colors.white};
  border-radius: 6px;
  padding: 7px 10px;

  ${withCssTransition(["color", "background"])};

  @media (hover: hover) {
    &:hover {
      background: rgb(244 245 246 / 0.2);
      color: ${theme.colors.white};
    }
  }

  ${({
  $motif
}) => $motif === HeaderMotif.DarkText && css`
      color: #212327;
    `}
  @media (hover: hover) {
    &:hover {
      color: ${theme.colors.primaryBlue};
    }
  }

  ${({
  $active,
  $motif
}) => $active && css`
      color: ${$motif === HeaderMotif.DarkText ? theme.colors.primaryBlue : theme.colors.white};
      background: ${$motif === HeaderMotif.DarkText ? "#f4f5f6" : "rgba(244, 245, 246, 0.2)"};
    `}

  ${({
  $isStartItem
}) => $isStartItem && css`
      background: transparent !important;
      padding: 7px 0;
    `}

    ${({
  $isHovered
}) => $isHovered && css`
      background: transparent !important;
      color: ${theme.colors.primaryBlue};
    `}

  ${({
  $menuIsClosed,
  $motif
}) => $menuIsClosed && css`
      color: ${$motif === HeaderMotif.DarkText ? theme.colors.black : theme.colors.white};
    `}

  &:focus-visible {
    color: ${theme.colors.primaryBlue};
  }
`;
const ArrowDownIcon = styled(StyleableSvgImage)`
  margin-right: 6px;
  transition: transform 0.2s linear;

  ${({
  $isOpen
}) => $isOpen && css`
      transform: rotate(180deg);
    `}
`;
const Item = styled.button`
  font-size: 14px;
  text-align: center;
  height: 100%;
  background: transparent;
  outline: none;
  border: none;
  padding: 0;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
`;
const RightWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10.5px;
`;
const ActionIcon = styled.div`
  cursor: pointer;
  color: ${theme.colors.white};
  ${withCssTransition(["color"])};

  ${({
  $motif
}) => $motif === HeaderMotif.DarkText && css`
      color: #212327;
    `}

  @media ${BREAKPOINTS.TOP_NAV_MOBILE_AND_DOWN} {
    .header-icon {
      width: 21px;
      height: 25px;
    }
  }

  &:focus-visible {
    color: ${theme.colors.primaryBlue};
  }
`;
const RightItem = styled.a`
  font-size: 14px;
  text-align: center;
  height: 100%;

  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${theme.colors.white};
  ${withCssTransition(["color"])};

  @media (hover: hover) {
    :hover {
      color: ${theme.colors.primaryBlue};
    }
  }

  ${({
  $motif
}) => $motif === HeaderMotif.DarkText && css`
      color: #212327;

      @media (hover: hover) {
        &:hover {
          color: ${theme.colors.primaryBlue};
        }
      }
    `}

  @media (hover: hover) and (pointer: fine) {
    &:focus-visible {
      color: ${theme.colors.primaryBlue};
    }
  }

  ${({
  $withMarginRight
}) => $withMarginRight && css`
      margin-right: 4px;
    `}

  @media ${BREAKPOINTS.TOP_NAV_MOBILE_AND_DOWN} {
    display: none;
  }
`;
const ModalWrap = styled(Container)`
  flex: 0;
  margin: 0 auto;
  position: absolute;
  z-index: ${theme.zIndex.header - 1};
  /* replace with theme of motif? */
  background-color: white;
`;
const ModalMenuWrap = styled(Container)`
  padding: 0 0 24px;
  max-width: 100%;
  margin: 24px auto;
`;
const OverflowWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 16px 48px;
  background-color: ${theme.colors.white};
  /* Safari view height changes depending on user interaction and url bar */
  height: calc(var(--vh, 1vh) * 100);
  &::-webkit-scrollbar {
    /* @TODO should hide scrollbar? test outside of storybook */
    display: none;
  }
`;
const ModalMenuInnerWrap = styled.div`
  top: 24px;
  z-index: ${theme.zIndex.modal};
  width: 100%;
  height: 100vh;

  ${({
  $modal
}) => {
  return $modal === ModalId.MobileMenu && css`
        margin: 0 auto;
      `;
}}
`;
const ModalInnerWrap = styled.div`
  position: absolute;
  top: 34px;
  z-index: ${theme.zIndex.modal};
  height: 100vh;

  ${({
  $modal
}) => {
  return $modal === ModalId.Account && css`
        right: 32px;

        @media ${BREAKPOINTS.TOP_NAV_MOBILE_AND_DOWN} {
          right: 10px;
        }
      `;
}}

  ${({
  $modal
}) => {
  return $modal === ModalId.Language && css`
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 754px;
        height: 332px;
      `;
}}
`;
const Divider = styled.div`
  width: 1px;
  height: 22px;
  background: rgb(0 0 0 / 0.15);
  margin: 0 8px 0 9px;
`;
const InnerWrap = styled.div`
  position: relative;
  align-items: center;
  display: flex;
  height: ${FIRST_LEVEL_MENU_HEIGHT}px;
  justify-content: space-between;
  max-width: 1200px;
  width: 100vw;

  ${ItemLabel}, ${LogoAnchor}, ${ActionIcon}, ${RightItem}, ${MenuButtonWrap}, ${Divider} {
    z-index: ${theme.zIndex.header} !important;
  }

  .hamburger-icon {
    ${withCssTransition(["color"])};
    color: ${theme.colors.white};

    ${({
  $motif
}) => $motif === HeaderMotif.DarkText && css`
        color: #212327;
      `}
  }
`;
const TransparentButton = styled.button`
  background: transparent;
  outline: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  height: 100%;

  ${({
  $shouldHideOnDesktop
}) => $shouldHideOnDesktop && css`
      @media ${BREAKPOINTS.TOP_NAV_MOBILE_UP} {
        display: none;
      }
    `}
`;
export { ANIMATION_TRANSITION, ActionIcon, ArrowDownIcon, Content, ContentContainer, Divider, InnerWrap, Item, ItemLabel, ItemsWrap, LeftWrap, LogoAnchor, ModalInnerWrap, ModalMenuInnerWrap, ModalMenuWrap, ModalWrap, OverflowWrapper, RightItem, RightWrap, StableGutterGlobalStyles, StyledContainer, TransparentButton, Wrap };
//# sourceMappingURL=HeaderContent.styles.mjs.map
